import { Injectable } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { FormField, QuestionGroup } from '../models/ropa.interface';
import { RopaFormValidators } from '../validators/ropa-form.validators';

@Injectable({
  providedIn: 'root',
})
export class RopaFormBuilderService {
  constructor(private fb: FormBuilder) {}

  /**
   * Creates a dynamic FormGroup based on the form structure
   */
  createFormGroup(
    questionGroups: QuestionGroup[],
    isEditMode: boolean = false
  ): FormGroup {
    const formControls: { [key: string]: FormControl } = {};

    questionGroups.forEach((group) => {
      this.processQuestionGroup(group, formControls, isEditMode);
    });

    return this.fb.group(formControls);
  }

  /**
   * Processes a question group and its subgroups recursively
   */
  private processQuestionGroup(
    group: QuestionGroup,
    formControls: { [key: string]: FormControl },
    isEditMode: boolean
  ): void {
    // Process fields in current group
    group.fields?.forEach((field) => {
      if (field.modelProperty) {
        const control = this.createFormControl(field, isEditMode);
        formControls[field.modelProperty] = control;
      }
    });

    // Process subgroups recursively
    group.subgroups?.forEach((subgroup) => {
      this.processQuestionGroup(subgroup, formControls, isEditMode);
    });
  }

  /**
   * Creates a FormControl with appropriate validators based on field type
   */
  private createFormControl(
    field: FormField,
    isEditMode: boolean
  ): FormControl {
    const initialValue = this.getInitialValue(field, isEditMode);
    const validators = this.getValidators(field);

    return new FormControl(initialValue, validators);
  }

  /**
   * Gets the initial value for a field
   */
  private getInitialValue(field: FormField, isEditMode: boolean): any {
    // Check if field has an existing answer (edit mode)
    if (isEditMode && (field as any).answer !== undefined) {
      const answer = (field as any).answer;
      
      if (field.type === 'dropdown' || field.type === 'checkbox') {
        if (Array.isArray(answer)) {
          return answer;
        }
        
        if (typeof answer === 'string') {
          if (answer.trim() === '') {
            return [];
          }
          
          try {
            const parsed = JSON.parse(answer);
            if (Array.isArray(parsed)) {
              return parsed;
            }
          } catch (e) {
          }
          
          return answer.split(',').map(v => v.trim()).filter(v => v !== '');
        }
        
        if (answer === null || answer === undefined) {
          return [];
        }
        
        return [answer];
      }
      
      return answer;
    }

    // Return default value based on field type
    switch (field.type) {
      case 'checkbox':
      case 'dropdown':
        return [];
      default:
        return '';
    }
  }

  /**
   * Gets validators for a field based on its type and requirements
   */
  private getValidators(field: FormField): any[] {
    const validators = [];

    if (field.required) {
      switch (field.type) {
        case 'checkbox':
        case 'dropdown':
          validators.push(RopaFormValidators.arrayMinLength(1));
          break;
        case 'text':
        case 'textarea':
          validators.push(
            Validators.required,
            RopaFormValidators.nonEmptyString()
          );
          break;
        default:
          validators.push(Validators.required);
          break;
      }
    }

    // Add specific validators
    if (field.type === 'email') {
      validators.push(Validators.email);
    }

    // Add max length validator for specific fields
    if (this.isLimitedSelectionField(field)) {
      validators.push(RopaFormValidators.arrayMaxLength(25));
    }

    return validators;
  }

  /**
   * Checks if a field has selection limitations
   */
  private isLimitedSelectionField(field: FormField): boolean {
    return (
      field.label === 'Owning Organization (Data Controller)' ||
      field.label ===
        'Select any and all of the AZ legal entity(ies) with whom the personal data is shared.'
    );
  }
}
